import { PaymentIcon } from 'react-svg-credit-card-payment-icons';
import type { StripePaymentMethodDto } from '@/features/billing/types';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

const getCardIconType = (brand: string): string => {
  const b = brand.toLowerCase().replace(/\s+/g, '');
  const map: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'AmericanExpress',
    americanexpress: 'AmericanExpress',
    american_express: 'AmericanExpress',
    discover: 'Discover',
    diners: 'DinersClub',
    diners_club: 'DinersClub',
    dinersclub: 'DinersClub',
    jcb: 'Jcb',
    unionpay: 'Unionpay',
    maestro: 'Maestro',
    elo: 'Elo',
    hiper: 'Hiper',
    hipercard: 'Hipercard',
    mir: 'Mir',
  };
  return map[b] ?? 'Generic';
};

type UpgradePaymentMethodSelectorProps = {
  paymentMethods: StripePaymentMethodDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  disabled?: boolean;
};

export default function UpgradePaymentMethodSelector({
  paymentMethods,
  selectedId,
  onSelect,
  disabled,
}: UpgradePaymentMethodSelectorProps) {
  const { t } = useTranslation('billing');

  if (paymentMethods.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-600">{t('paymentMethodSelector.payWithSaved')}</p>

      <div className="space-y-2">
        {paymentMethods.map((card) => (
          <button
            key={card.id}
            type="button"
            onClick={() => !disabled && onSelect(card.id)}
            disabled={disabled}
            className={cn(
              'w-full flex items-center gap-4 p-4 border rounded-lg text-left transition-colors',
              selectedId === card.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300',
              disabled && 'opacity-60 cursor-not-allowed',
            )}
          >
            <PaymentIcon
              type={getCardIconType(card.brand) as React.ComponentProps<typeof PaymentIcon>['type']}
              format="flatRounded"
              width={40}
              style={{ display: 'block' }}
              aria-label={`${card.brand}`}
            />
            <div>
              <p className="font-medium">
                {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} ****{card.last4}
              </p>
              <p className="text-sm text-gray-500">
                {t('paymentMethodSelector.expires')} {card.expMonth.toString().padStart(2, '0')}/
                {card.expYear}
                {card.isDefault && (
                  <span className="ml-2 text-blue-600 font-medium">
                    {t('paymentMethodSelector.default')}
                  </span>
                )}
              </p>
            </div>
            {selectedId === card.id && (
              <div className="ml-auto size-5 rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
